import { mountChrome } from './chrome.js?v=1789681238';
import { catalog, renderGrid, wireQuickAdd, CONCERN_LABEL, shopHref, assetURL } from './catalog.js?v=1789571604';
import { initMotion } from './motion.js?v=1789571604';

mountChrome();
wireQuickAdd();

/* Fallback only. The real list is derived from the catalog below, because this hardcoded
   copy had already drifted: it was missing SkinCeuticals and AQUAFLOR entirely and ran in a
   different order from the nav menu, which is what a customer noticed. */
const BRANDS = ['G.M. Collin', 'Environ', 'ISDIN', 'EltaMD', '111SKIN', 'OMNILUX', 'BIOPELLE', 'Lemon & Beaker', 'La Fleur by Priti', 'Aloe Up', 'LPS4MEN', 'Skinade'];

/* guide pages are real files on the static build but a single Shopify page */
const guideHref = href => {
  const base = document.body.dataset.base || '';
  return window.NEWEL_ON_SHOPIFY === true ? '/pages/skincare-guides' : base + href;
};

const GUIDES = [
  ['The Aging & Fine Lines Guide', 'What actually softens fine lines: retinaldehyde, peptides, and the order to layer them.', 'guides/anti-aging-fine-lines.html'],
  ['The Dryness & Dehydration Guide', 'Dry and dehydrated are not the same problem. Here is how to tell, and what each one needs.', 'guides/dry-dehydrated-skin.html'],
  ['Fading Dark Spots Safely', 'Brightening without bleaching: the ingredients we trust and the ones we skip.', 'guides/dark-spots-hyperpigmentation.html'],
];

(async () => {
  /* home.js is the entry script for the DEFAULT layout, which Shopify uses for the product
     template, the concern guides, cart, search and 404 as well as the homepage. On all of
     those, none of the four blocks below exist, yet this used to `await catalog()`
     unconditionally: 65 KB over the wire and 481 KB decoded, parsed on the main thread, to
     render nothing. That is a real cost on a phone.

     Bail before touching the network unless there is actually something here to fill. The
     header brand menu no longer needs the catalogue either (see BUILD_BRANDS in chrome.js),
     and search still loads it lazily when the overlay is opened. */
  const NEEDS_CATALOG = '[data-grid],[data-concern-grid],[data-marquee],[data-guide-grid]';
  /* initMotion() MUST still run on those pages: it drives every `data-reveal` element, which
     starts at opacity 0. Returning before it would leave product pages and guides with
     permanently invisible content. */
  if (!document.querySelector(NEEDS_CATALOG)) { initMotion(); return; }

  const all = await catalog();

  const best = all.filter(p => p.best && p.av);
  const pool = best.length >= 8 ? best : all.filter(p => p.av);
  document.querySelectorAll('[data-grid]').forEach(el => {
    const limit = Number(el.dataset.limit) || 8;
    const set = el.dataset.grid === 'new'
      ? all.filter(p => p.new && p.av).concat(all.filter(p => p.av)).slice(0, limit)
      : pool.slice(0, limit);
    renderGrid(el, set);
  });

  /* concern tiles with live counts */
  const CONCERN_NOTE = {
    aging: 'Retinoids, Peptides, Daily SPF',
    'dark-spots': 'Vitamin C, Brightening Acids',
    dryness: 'Hyaluronic Acid, Barrier Repair',
    sensitivity: 'Calming, Fragrance-Free',
    acne: 'Salicylic, Niacinamide, Oil Control',
    dullness: 'Gentle Exfoliation, Glow',
    sun: 'Broad-Spectrum, Mineral & Chemical',
    'post-treatment': 'Recovery After Peels & Lasers',
  };
  /* the category that best *illustrates* each concern - without this, three tiles in a
     row all landed on near-identical EltaMD sunscreens */
  const CONCERN_CAT = {
    aging: 'Serums', 'dark-spots': 'Serums', dryness: 'Moisturizers',
    sensitivity: 'Cleansers', acne: 'Cleansers', dullness: 'Exfoliants',
    sun: 'SPF & Sun', 'post-treatment': 'Masks',
  };
  const cg = document.querySelector('[data-concern-grid]');
  if (cg) {
    const used = new Set(), usedBrands = new Set();
    /* Compare the tile PATH, never the tile URL. Tile URLs carry a ?v=<content hash> cache
       buster, so the raw value ends in '...-0.webp?v=1789571604' and the '-0.webp' test below
       was false for EVERY product in the catalogue. Every one of the eight tiles then fell
       through to the last resort, which took whatever sorted first in each concern set: the
       same $18 Aloe Up spray appeared on Fine Lines, Sensitivity AND Sun Protection, and two
       near-identical EltaMD sunscreens took Dark Spots and Acne. */
    const stem = p => (p.tile || '').split('?')[0];
    /* what the product is worth, used to break ties between equally qualified candidates */
    const value = p => Math.max(0, +p.p || 0, ...(p.v || []).map(v => +v.p || 0));
    cg.innerHTML = Object.entries(CONCERN_LABEL).map(([key, label], i) => {
      const set = all.filter(p => p.con.includes(key) && p.av);
      /* one distinct product per tile, and no two tiles from the same brand if we can
         help it, so the grid reads as eight different things */
      /* -0 tiles are the real packshots; a -1 tile means the front shot was a scene,
         and a model's face in a concern tile reads as stock photography */
      const ok = p => stem(p).endsWith('-0.webp') && !used.has(p.h);
      const fresh = p => ok(p) && !usedBrands.has(p.b);
      const onCat = p => p.cat?.includes(CONCERN_CAT[key]);
      /* Among candidates that qualify at the same level, show the most valuable one. This
         grid is the shop window for the whole store, and taking whichever product happened
         to sort first is how an $18 spray ended up fronting it. */
      const top = f => set.filter(f).sort((a, b) => value(b) - value(a))[0];
      const hero = top(p => fresh(p) && onCat(p) && p.best)
                || top(p => fresh(p) && onCat(p))
                || top(p => fresh(p) && p.best)
                || top(fresh)
                || top(p => ok(p) && onCat(p))
                || top(ok)
                /* Even the last resort must not repeat a product the grid already shows.
                   Without this guard it is the branch that produced the duplicates. */
                || top(p => !used.has(p.h) && (p.tile || p.img?.length));
      if (hero) { used.add(hero.h); usedBrands.add(hero.b); }
      /* No "from $X" here. It was a MINIMUM across the whole concern set, and a $3 Aloe Up
         lip balm was setting the price on four of the eight tiles against medians of $54 to
         $102, so the number was true and off by 16x to 34x from what anyone actually buys.
         It also reads discount-bin on a store whose whole argument is professional-grade.
         (Contrast Priti's Picks, where the price belongs to ONE named product and so
         qualifies the click honestly. Keep it there, not here.) */
      /* These eight thumbnails are NOT lazy. The tile starts at opacity 0 waiting on its
         GSAP reveal, and Chrome does not fetch a lazy image inside an invisible element, so
         the reveal fired and the boxes appeared with permanently blank image slots. Reported
         from the live site as "in these 8 boxes its blank". Eight images is a cheap fetch and
         this is a primary navigation block; width/height keep the slot reserved so nothing
         shifts when they land. */
      return `<a class="concern-tile" href="${shopHref(`con=${key}`)}" data-reveal="${i % 4}">
        <span class="concern-tile__no">${String(i + 1).padStart(2, '0')}</span>
        <span class="concern-tile__thumb">${hero ? `<img src="${assetURL(hero.tile || hero.img[0])}" alt="" decoding="async" width="1000" height="1000">` : ''}</span>
        <span class="concern-tile__body">
          <h3>${label}</h3>
          <span class="concern-tile__note">${CONCERN_NOTE[key] || ''}</span>
        </span>
        <span class="concern-tile__meta">
          <span class="count">${set.length} products</span>
        </span>
      </a>`;
    }).join('');
  }

  /* brand marquee (duplicated for a seamless -50% loop) */
  const m = document.querySelector('[data-marquee]');
  if (m) {
    /* order by how many products each brand actually has, so the strip matches the menu
       and nothing is hand-promoted. `all` is the catalog already loaded above. */
    const counts = new Map();
    (all || []).forEach(p => p.b && counts.set(p.b, (counts.get(p.b) || 0) + 1));
    const names = counts.size
      ? [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([n]) => n)
      : BRANDS;
    m.innerHTML = [...names, ...names].map(b => `<span>${b}</span>`).join('');
  }

  /* guides */
  const gg = document.querySelector('[data-guide-grid]');
  if (gg) gg.innerHTML = GUIDES.map(([t, d, href], i) =>
    `<a class="guide-card" href="${guideHref(href)}" data-reveal="${i}"><h3>${t}</h3><p>${d}</p><span class="more">Read →</span></a>`).join('');

  initMotion();
})();

/* See the note in pages.js: on Shopify this form posts natively and we stay out
   of the way; locally there is no endpoint and we say so. */
document.addEventListener('submit', e => {
  if (!e.target.matches('.newsletter-form')) return;
  if (e.target.dataset.native === '1') return;
  e.preventDefault();
  const form = e.target;
  const email = form.querySelector('input[type=email]');
  const consent = form.querySelector('input[type=checkbox]');
  /* Never wipe the form on a failed check - the visitor loses what they typed and
     cannot retry without reloading. Flag the offending control and keep the rest. */
  form.querySelectorAll('.field-error').forEach(n => n.remove());
  [email, consent].forEach(f => f && f.removeAttribute('aria-invalid'));
  const problems = [];
  if (!email.checkValidity()) problems.push([email, 'Please enter a valid email address.']);
  if (!consent.checked) problems.push([consent, 'Please tick the box to receive emails.']);
  if (problems.length) {
    problems.forEach(([f, text]) => {
      f.setAttribute('aria-invalid', 'true');
      const msg = document.createElement('p');
      msg.className = 'field-error';
      msg.textContent = text;
      (f.closest('label') || f.parentElement).appendChild(msg);
    });
    problems[0][0].focus();
    return;
  }
  form.innerHTML = '<p class="lede" style="text-align:center">Local preview build, so nothing was sent. On the live store this subscribes you to Skin Notes.</p>';
});
